import React, { useCallback, useEffect, useState } from 'react';
import AttributesPanel from '../../ProjectDetail/panels/AttributesPanel';
import { apiService } from '../../../services/api';
import { errorText, useNexusProjectRefresh, useTabError } from './shared';

const order = (attribute) => (typeof attribute.sort_order === 'number' ? attribute.sort_order : 0);
const sortAttributes = (items) => [...items].sort((a, b) => order(a) - order(b) || (a.id || 0) - (b.id || 0));
const list = (data) => (Array.isArray(data) ? data : []);

// Tableau des attributs du projet : cibles, statuts et leur historique.
const AttributesTab = ({ projectId }) => {
  const [attributes, setAttributes] = useState([]);
  const [history, setHistory] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const { error, fail, attempt } = useTabError();

  const loadAttributes = useCallback(async () => {
    setLoading(true);
    try {
      setAttributes(sortAttributes(list(await apiService.getProjectAttributes(projectId))));
      setLoadError(null);
    } catch (err) {
      setAttributes([]);
      setLoadError(err?.message || 'Erreur lors du chargement des attributs.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistory(list(await apiService.getProjectAttributeHistory(projectId, 200)));
    } catch (err) {
      setHistory([]);
      fail(`Historique des attributs : ${errorText(err)}`);
    } finally {
      setHistoryLoading(false);
    }
  }, [projectId, fail]);

  const loadTargets = useCallback(async () => {
    try {
      setTargets(list(await apiService.getProjectAttributeTargets(projectId)));
    } catch (err) {
      setTargets([]);
      fail(`Cibles de la roadmap : ${errorText(err)}`);
    }
  }, [projectId, fail]);

  useEffect(() => {
    loadAttributes();
    loadHistory();
    loadTargets();
  }, [loadAttributes, loadHistory, loadTargets]);

  useNexusProjectRefresh(projectId, (entity) => {
    if (entity !== 'attributes') return;
    loadAttributes();
    loadHistory();
    loadTargets();
  });

  const replaceAttribute = useCallback((updated) => {
    setAttributes((prev) => sortAttributes(prev.map((attr) => (attr.id === updated.id ? updated : attr))));
  }, []);

  const addAttribute = useCallback((payload) => attempt("Création de l'attribut", async () => {
    const created = await apiService.createProjectAttribute(projectId, payload);
    setAttributes((prev) => sortAttributes([...prev, created]));
    await loadHistory();
    return created;
  }), [attempt, projectId, loadHistory]);

  const updateAttribute = useCallback((attributeId, payload) => attempt("Mise à jour de l'attribut", async () => {
    const updated = await apiService.updateProjectAttribute(projectId, attributeId, payload);
    replaceAttribute(updated);
    await loadHistory();
    return updated;
  }), [attempt, projectId, replaceAttribute, loadHistory]);

  const updateStatus = useCallback((attributeId, status, comment, changedAt) => attempt('Changement de statut', async () => {
    const payload = { status, comment };
    if (changedAt) payload.history_timestamp = new Date(changedAt).toISOString();
    const updated = await apiService.updateProjectAttributeStatus(projectId, attributeId, payload);
    replaceAttribute(updated);
    await loadHistory();
    return updated;
  }), [attempt, projectId, replaceAttribute, loadHistory]);

  const deleteAttribute = useCallback((attributeId) => attempt("Suppression de l'attribut", async () => {
    await apiService.deleteProjectAttribute(projectId, attributeId);
    setAttributes((prev) => prev.filter((attr) => attr.id !== attributeId));
    await loadHistory();
    await loadTargets();
  }), [attempt, projectId, loadHistory, loadTargets]);

  const updateHistoryEntry = useCallback((attributeId, historyId, payload) => attempt("Mise à jour de l'historique", async () => {
    await apiService.updateProjectAttributeHistoryEntry(projectId, attributeId, historyId, payload);
    await loadHistory();
  }), [attempt, projectId, loadHistory]);

  const deleteHistoryEntry = useCallback((attributeId, historyId) => attempt("Suppression de l'historique", async () => {
    await apiService.deleteProjectAttributeHistoryEntry(projectId, attributeId, historyId);
    await loadHistory();
  }), [attempt, projectId, loadHistory]);

  return (
    <AttributesPanel
      attributes={attributes}
      history={history}
      roadmapTargets={targets}
      error={loadError || error}
      isLoading={loading}
      historyLoading={historyLoading}
      onAddAttribute={addAttribute}
      onUpdateAttribute={updateAttribute}
      onUpdateStatus={updateStatus}
      onDeleteAttribute={deleteAttribute}
      onUpdateHistoryEntry={updateHistoryEntry}
      onDeleteHistoryEntry={deleteHistoryEntry}
    />
  );
};

export default AttributesTab;
